import debounce from 'lodash.debounce'

import { IcdCode } from '../../constants/client_types'
import { isProvider } from '../../constants/theme'
import { getOnlinePresence } from '../../services/helpers/UserPresenceUpdated'

import STREAM_DATA_QUERY from './StreamDataQuery.gql'
import WEB_SOCKET_QUERY from './WebSocketQuery.gql'

export let apolloMutationCount = 0
let subscriptions: string[] = []

export interface ProgressNoteData {
  auditComment?: string
  billingLabel?: string
  edit?: boolean
  encounterId?: string | null
  message: string | null
  rawIcdCodes: IcdCode[]
  resolutionTags: string[]
}

export interface StreamDataItem {
  __typename: string
  editProgressNote?: Partial<ProgressNoteData> | null
  hasBeenViewedThisSession?: boolean | null
  isBeingViewed?: boolean | null
  isScrolledToBottom?: boolean | null
  key: string
  messageInputValue?: string | null
  messageReceived?: boolean | null
  progressNote?: Partial<ProgressNoteData> | null
  typingMessage: string | null
}

export interface StreamDataClientProps {
  __typename: string
  streamDataItems: StreamDataItem[]
}

export const streamDataDefaultProps: StreamDataClientProps = {
  __typename: 'StreamData',
  streamDataItems: [],
}

const baseStream = {
  __typename: 'StreamDataItem',
  editProgressNote: null,
  hasBeenViewedThisSession: false,
  isBeingViewed: false,
  isScrolledToBottom: false,
  messageInputValue: null,
  messageReceived: null,
  progressNote: null,
  typingMessage: null,
}

const updateStreamData = (input, cache) => {
  const { streamData } = cache.readQuery({ query: STREAM_DATA_QUERY })
  const newStreams = [...streamData.streamDataItems]
  const [streamId] = Object.keys(input)
  const incomingStreamData = input[streamId]
  let index = 0
  const currentStream = newStreams.find(
    (s: Partial<StreamDataItem>, i: number) => {
      index = i
      return s.key === streamId
    },
  )

  const updatedStream = {
    ...baseStream,
    ...currentStream,
    ...incomingStreamData,
    key: streamId,
  }
  if (currentStream) {
    newStreams[index] = updatedStream
  } else {
    newStreams.push(updatedStream)
  }

  cache.writeQuery({
    data: {
      streamData: {
        __typename: streamDataDefaultProps.__typename,
        streamDataItems: newStreams,
      },
    },
    query: STREAM_DATA_QUERY,
  })
  apolloMutationCount++
  return null
}

export interface WebSocketClientProps {
  __typename: string
  encounterReload: boolean
  providerReload: boolean
  videoNowInitiated: string | null
}

export const webSocketDefaultProps: WebSocketClientProps = {
  __typename: 'WebSocket',
  encounterReload: false,
  providerReload: false,
  videoNowInitiated: null,
}

const updateWebSocket = (input, cache) => {
  const { webSocket } = cache.readQuery({ query: WEB_SOCKET_QUERY })
  const data = {
    webSocket: {
      __typename: webSocketDefaultProps.__typename,
      ...webSocket,
      ...input,
    },
  }
  cache.writeQuery({
    data,
    query: WEB_SOCKET_QUERY,
  })
  apolloMutationCount++
  return null
}

const debouncedSubscribe = debounce(subscriptions => {
  const subscribe = (window as any).subscribe
  if (subscribe) {
    subscribe(subscriptions)
  }
}, 333)

// this is a hook so that we can use the @client directive to subscribe users
// to the presence web socket event which triggers a web socket event that
// writes directly to the proper cache's fragment
const isOnlineFn = identifier => (model, _args, ctx) => {
  if (isProvider()) {
    const channel = `presence:${identifier}:${model.id}`
    if (!subscriptions.includes(channel)) {
      subscriptions = subscriptions.concat(channel)
      debouncedSubscribe(subscriptions)
    }
  }
  const { data } = getOnlinePresence(ctx.cache, identifier, model.id)
  return (data && data.isOnline) || false
}

export const resolvers = {
  Mutation: {
    updateStreamData: (_, { input }, { cache }) =>
      updateStreamData(input, cache),
    updateWebSocket: (_, { input }, { cache }) => updateWebSocket(input, cache),
  },
  Patient: { isOnline: isOnlineFn('patient') },
  PatientDependent: { isOnline: isOnlineFn('patient') },
  Query: {
    streamData: () => streamDataDefaultProps,
    webSocket: () => webSocketDefaultProps,
  },
}
